import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi2';

interface SessionNavigationProps {
  previousSessionId: string | null;
  nextSessionId: string | null;
  // Path builder for a target session; defaults to the config tab.
  getNavigationPath?: (targetSessionId: string) => string;
}

export default function SessionNavigation({
  previousSessionId,
  nextSessionId,
  getNavigationPath = (targetSessionId) => `/sessions/${targetSessionId}/config`,
}: SessionNavigationProps): ReactElement | null {
  const navigate = useNavigate();

  if (!previousSessionId && !nextSessionId) {
    return null;
  }

  const buttonClass = 'cr-flex cr-items-center cr-justify-center cr-rounded-md cr-bg-gray-200 '
    + 'cr-p-2 cr-text-blue-900 cr-transition-colors cr-duration-200 hover:cr-bg-cirrusBlue-50 '
    + 'disabled:cr-cursor-not-allowed disabled:cr-opacity-40';

  return (
    // Below lg this collapses to a bare pair of chevrons — the "Sessions" /
    // "Prev" / "Next" captions are what made it a block-sized control, and the
    // chevrons plus their titles carry the same meaning on a phone.
    <div className="cr-inline-flex cr-flex-col cr-items-center cr-gap-1.5">
      <div className="cr-hidden cr-text-sm cr-font-medium cr-text-blue-900 cr-font-main lg:cr-block">Sessions</div>
      <div className="cr-flex cr-gap-2">
        <div className="cr-flex cr-flex-col cr-items-center cr-gap-0.5">
          <button
            onClick={() => previousSessionId && navigate(getNavigationPath(previousSessionId))}
            disabled={!previousSessionId}
            className={buttonClass}
            title="Previous Session"
            aria-label="Previous Session"
          >
            <HiChevronLeft className="cr-h-5 cr-w-5" />
          </button>
          <div className="cr-hidden cr-text-[13px] cr-text-blue-900 cr-font-main lg:cr-block">Prev</div>
        </div>
        <div className="cr-flex cr-flex-col cr-items-center cr-gap-0.5">
          <button
            onClick={() => nextSessionId && navigate(getNavigationPath(nextSessionId))}
            disabled={!nextSessionId}
            className={buttonClass}
            title="Next Session"
            aria-label="Next Session"
          >
            <HiChevronRight className="cr-h-5 cr-w-5" />
          </button>
          <div className="cr-hidden cr-text-[13px] cr-text-blue-900 cr-font-main lg:cr-block">Next</div>
        </div>
      </div>
    </div>
  );
}
